import { PathValue, UseFormReturn, useWatch } from 'react-hook-form';
import { PropertyValidationInformation } from '../../../util/PropertyValidationInformation';
import { AutocompleteTextFieldProps } from '../../../../../../react-commons/components/form/inputs/Form_ReferenceInput';
import { useNestedDialogFunction } from '../../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import {
    WorkflowRepresentationEditDialog,
} from '../../../page-components/project/reusable-elements/workflow-representation/WorkflowRepresentationEditDialog';
import {
    WorkflowRepresentationCreationDialog,
} from '../../../page-components/project/reusable-elements/workflow-representation/WorkflowRepresentationCreationDialog';
import { Form_AutocompleteReferenceTextField } from './Form_AutocompleteReferenceTextField';
import React from 'react';
import {
    useWorkflowRepresentationActions,
} from '../../../../resource/resources/artifacts/use_case/representation/workflowRepresentationResource';
import { useSystemInformationActions } from '../../../../resource/services/systemInformationService';

export const Form_WorkflowRepresentationTextField = <T extends object, N extends boolean>({
                                                                                              projectVersionId,
                                                                                              projectId,
                                                                                              nullable,
                                                                                              name,
                                                                                              form,
                                                                                              propertyValidationInformation,
                                                                                              readOnly,
                                                                                          }: {
    projectId: string | undefined | null;
    projectVersionId: string | undefined | null;
    form: UseFormReturn<T>;
    propertyValidationInformation: PropertyValidationInformation;
    readOnly: boolean;
} & AutocompleteTextFieldProps<T, N>) => {
    const objectId = useWatch({ control: form.control, name });


    const nestedDialog = useNestedDialogFunction();

    const workflowRepresentationActions = useWorkflowRepresentationActions();
    const systemInformationActions = useSystemInformationActions();
    const workflowRepresentationsQuery = workflowRepresentationActions.useGetAllResources(projectVersionId);
    const propertyDetails = systemInformationActions.useGetPropertyDetails(
        propertyValidationInformation.propertyIdentifier,
        propertyValidationInformation.resourceType,
    );
    const propertyValidation = systemInformationActions.useGetPropertyValidationWithId(
        propertyValidationInformation.propertyIdentifier,
        propertyValidationInformation.resourceType,
        propertyValidationInformation.resourceId ?? '',
    );

    const handleChange = (id: string | null) => {
        form.setValue(name, id as PathValue<T, typeof name>, { shouldDirty: true, shouldValidate: true });
    };

    const onEdit = () => {
        nestedDialog.showNestedDialog(
            <WorkflowRepresentationEditDialog
                onClose={nestedDialog.closeNestedDialog}
                objectId={objectId}
                versionedInId={projectVersionId ?? ''}
                projectId={projectId ?? ''}
            />,
        );
    };

    const onAdd = () => {
        nestedDialog.showNestedDialog(
            <WorkflowRepresentationCreationDialog
                onClose={nestedDialog.closeNestedDialog}
                parentId={projectVersionId ?? ''}
                onCreated={(_, newObjectId) => {
                    if (handleChange) {
                        handleChange(newObjectId);
                    }
                }}
            />,
        );
    };

    return (
        <Form_AutocompleteReferenceTextField
            control={form.control}
            nullable={nullable}
            name={name}
            readOnly={readOnly}
            propertyValidation={propertyValidation}
            propertyDetails={propertyDetails}
            label={'Workflow Representation'}
            query={workflowRepresentationsQuery}
            idSelector={'objectId'}
            typeKey={'type'}
            labelAccessor={'name'}
            onChange={_ => form.trigger()}
            onAdd={onAdd}
            onEdit={onEdit}
        />
    );
};